import type { Invoice, InvoiceItem, InvoiceStatus, PaymentRecord } from '../types';
import {
  calculateBalanceDueWithPayments,
  calculatePaymentTotals,
  calculateSubtotal,
  calculateTax,
  calculateTotal,
} from './calculations';

export interface InvoiceEditorFinancialSummary {
  subtotal: number;
  taxAmount: number;
  total: number;
  amountPaid: number;
  balanceDue: number;
}

interface EditorFinancialInput {
  items: InvoiceItem[];
  existingPayments?: PaymentRecord[];
  storedAmountPaid?: number;
  pendingDepositAmount?: number;
}

export const calculateInvoiceEditorFinancials = ({
  items,
  existingPayments = [],
  storedAmountPaid = 0,
  pendingDepositAmount = 0,
}: EditorFinancialInput): InvoiceEditorFinancialSummary => {
  const subtotal = calculateSubtotal(items);
  const taxAmount = calculateTax(subtotal, 0.1, items);
  const total = calculateTotal(subtotal, taxAmount);

  if (existingPayments.length > 0) {
    const totals = calculatePaymentTotals(existingPayments);
    return {
      subtotal,
      taxAmount,
      total,
      amountPaid: totals.totalPaid - totals.totalRefunded,
      balanceDue: calculateBalanceDueWithPayments(
        total,
        totals.totalPaid,
        totals.totalRefunded,
        totals.totalAdjustments,
      ),
    };
  }

  const amountPaid = Number(storedAmountPaid || 0) + Number(pendingDepositAmount || 0);
  return {
    subtotal,
    taxAmount,
    total,
    amountPaid,
    balanceDue: calculateBalanceDueWithPayments(total, amountPaid, 0, 0),
  };
};

const isPastDueDate = (dueDate?: string): boolean => {
  if (!dueDate) return false;
  const due = new Date(dueDate);
  const today = new Date();
  due.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return due < today;
};

export const deriveInvoicePaymentState = (
  invoice: Pick<Invoice, 'total' | 'status' | 'dueDate'>,
  invoicePayments: PaymentRecord[],
): { amountPaid: number; balanceDue: number; status: InvoiceStatus } => {
  const totals = calculatePaymentTotals(invoicePayments);
  const amountPaid = totals.totalPaid - totals.totalRefunded;
  const balanceDue = calculateBalanceDueWithPayments(
    invoice.total,
    totals.totalPaid,
    totals.totalRefunded,
    totals.totalAdjustments,
  );

  let status: InvoiceStatus = invoice.status;
  if (!['cancelled', 'void'].includes(invoice.status)) {
    if (balanceDue < 0) status = 'overpaid';
    else if (balanceDue === 0) status = 'paid';
    else if (isPastDueDate(invoice.dueDate)) status = 'overdue';
    else if (invoicePayments.some(payment => payment.type === 'deposit')) status = 'deposit_paid';
    else if (invoicePayments.some(payment => payment.type === 'part_payment' || payment.type === 'final_payment')) status = 'part_paid';
  }

  return { amountPaid, balanceDue, status };
};